/**
 * Canonical, side-effect-free contract for the founder-facing card live state.
 *
 * This module deliberately owns no reads. The gateway supplies one coherent
 * run tuple plus independently-read human, queue, delivery, and container
 * facts. The resolver answers only "what is the agent doing?"; delivery and
 * container liveness stay orthogonal.
 */
import { z } from "zod";

export const liveStateNames = [
  "working",
  "stalled",
  "blocked",
  "yourTurn",
  "queued",
  "failed",
  "finished",
  "idle",
  "unknown",
] as const;
export const liveStateSchema = z.enum(liveStateNames);
export type LiveState = z.infer<typeof liveStateSchema>;

export const liveStateRunStatuses = [
  "starting",
  "running",
  "waiting_for_human",
  "done",
  "failed",
  "interrupted",
  "timeout",
] as const;
export const liveStateRunStatusSchema = z.enum(liveStateRunStatuses);
export type LiveStateRunStatus = z.infer<typeof liveStateRunStatusSchema>;

const ACTIVE_RUN_STATUSES = new Set<LiveStateRunStatus>([
  "starting",
  "running",
]);
const isActiveRunStatus = (status: LiveStateRunStatus): boolean =>
  ACTIVE_RUN_STATUSES.has(status);

export const LIVE_STATE_RECENT_FAILURE_MS = 24 * 60 * 60 * 1000;
export const LIVE_STATE_MAX_STALE_AFTER_MS = 2_147_483_647;

const liveStateStaleAfterMsSchema = z
  .number()
  .int()
  .min(1)
  .max(LIVE_STATE_MAX_STALE_AFTER_MS);

const liveStateClientIdempotencyKeySchema = z.union([
  z.string().uuid(),
  z.string().regex(/^ask:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i),
]);

function addMilliseconds(timestamp: string, milliseconds: number): string {
  return new Date(Date.parse(timestamp) + milliseconds).toISOString();
}
export const liveStateRunSchema = z
  .object({
    id: z.string().uuid(),
    sessionId: z.string().uuid(),
    status: liveStateRunStatusSchema,
    startedAt: z.string().datetime(),
    endedAt: z.string().datetime().nullable(),
    heartbeatAt: z.string().datetime().nullable(),
    lastOutputAt: z.string().datetime().nullable(),
    // The gateway supplies the exact values used to derive stale booleans.
    // They are wire facts so clients can validate freshness at computedAt.
    heartbeatStaleAfterMs: liveStateStaleAfterMsSchema,
    outputStaleAfterMs: liveStateStaleAfterMsSchema,
    userMessageId: z.string().uuid().nullable(),
    /** Exact client send identity; optional for rolling-deploy compatibility. */
    clientIdempotencyKey: liveStateClientIdempotencyKeySchema.nullable().optional(),
  })
  .superRefine((value, context) => {
    const active = isActiveRunStatus(value.status);
    if (active !== (value.endedAt === null)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: active
          ? "an active run cannot have endedAt"
          : "a terminal run requires endedAt",
        path: ["endedAt"],
      });
    }

    const startedAtMs = Date.parse(value.startedAt);
    const endedAtMs = value.endedAt === null ? null : Date.parse(value.endedAt);
    for (const [field, timestamp] of [
      ["endedAt", value.endedAt],
      ["heartbeatAt", value.heartbeatAt],
      ["lastOutputAt", value.lastOutputAt],
    ] as const) {
      if (timestamp !== null && Date.parse(timestamp) < startedAtMs) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${field} cannot precede startedAt`,
          path: [field],
        });
      }
      if (
        timestamp !== null &&
        endedAtMs !== null &&
        Date.parse(timestamp) > endedAtMs
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${field} cannot follow endedAt`,
          path: [field],
        });
      }
    }
  });
export type LiveStateRun = z.infer<typeof liveStateRunSchema>;

function futureRunTimestampField(
  run: LiveStateRun,
  nowMs: number,
): string | null {
  for (const [field, timestamp] of [
    ["started_at", run.startedAt],
    ["ended_at", run.endedAt],
    ["heartbeat_at", run.heartbeatAt],
    ["last_output_at", run.lastOutputAt],
  ] as const) {
    if (timestamp !== null && Date.parse(timestamp) > nowMs) {
      return field;
    }
  }
  return null;
}

/**
 * Run-derived facts travel as one tuple. failed includes the DB non-zero exit
 * rule, which cannot be reconstructed from the public run shape.
 */
export const liveStateRunFactsSchema = z
  .object({
    run: liveStateRunSchema,
    active: z.boolean(),
    failed: z.boolean(),
    heartbeatStale: z.boolean(),
    outputStale: z.boolean(),
  })
  .superRefine((value, context) => {
    const statusIsActive = isActiveRunStatus(value.run.status);
    if (value.active !== statusIsActive) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "run.active must agree with the run status",
        path: ["active"],
      });
    }
    if (!value.active && (value.heartbeatStale || value.outputStale)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "staleness facts belong only to an active run",
        path: ["heartbeatStale"],
      });
    }
    if (
      (value.run.status === "failed" || value.run.status === "timeout") &&
      !value.failed
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "failed and timeout statuses require the failed fact",
        path: ["failed"],
      });
    }
    if (value.active && value.failed) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "an active run cannot be an ended-run failure",
        path: ["failed"],
      });
    }
  });
export type LiveStateRunFacts = z.infer<typeof liveStateRunFactsSchema>;

export const liveStateQueueSchema = z
  .object({
    queued: z.boolean(),
    reason: z.string().min(1).nullable(),
    etaAt: z.string().datetime().nullable(),
  })
  .superRefine((value, context) => {
    if (!value.queued && (value.reason !== null || value.etaAt !== null)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "an inactive queue cannot carry reason or etaAt",
        path: ["queued"],
      });
    }
  });
export type LiveStateQueue = z.infer<typeof liveStateQueueSchema>;

/**
 * Facts required by the frozen ladder. Every branch that has no timestamp in
 * its run or queue tuple receives an explicit authoritative transition fact.
 */
export const liveStateFactsSchema = z
  .object({
    unreadableReason: z.string().min(1).nullable(),
    blocked: z.boolean(),
    blockedSince: z.string().datetime().nullable(),
    run: liveStateRunFactsSchema.nullable(),
    queue: liveStateQueueSchema,
    queuedSince: z.string().datetime().nullable(),
    yourTurn: z.boolean(),
    yourTurnSince: z.string().datetime().nullable(),
    everStarted: z.boolean(),
    finishedSince: z.string().datetime().nullable(),
    idleSince: z.string().datetime().nullable(),
  })
  .superRefine((value, context) => {
    const rejectOrphanTimestamp = (
      signal: boolean,
      timestamp: string | null,
      path: "blockedSince" | "queuedSince" | "yourTurnSince",
      label: string,
    ) => {
      if (!signal && timestamp !== null) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${path} cannot exist when ${label} is false`,
          path: [path],
        });
      }
    };

    rejectOrphanTimestamp(
      value.blocked,
      value.blockedSince,
      "blockedSince",
      "blocked",
    );
    rejectOrphanTimestamp(
      value.queue.queued,
      value.queuedSince,
      "queuedSince",
      "queue.queued",
    );
    rejectOrphanTimestamp(
      value.yourTurn,
      value.yourTurnSince,
      "yourTurnSince",
      "yourTurn",
    );

    if (value.run !== null && !value.everStarted) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "a run tuple requires everStarted",
        path: ["everStarted"],
      });
    }
    if (value.yourTurn && !value.everStarted) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "yourTurn requires prior work",
        path: ["everStarted"],
      });
    }
    if (value.run !== null && value.finishedSince !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "run.endedAt owns the finished transition when a run is present",
        path: ["finishedSince"],
      });
    }
    if (!value.everStarted && value.finishedSince !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "finishedSince requires everStarted",
        path: ["finishedSince"],
      });
    }

    const canBeIdle =
      !value.everStarted &&
      value.unreadableReason === null &&
      !value.blocked &&
      value.run === null &&
      !value.queue.queued &&
      !value.yourTurn;
    if (!canBeIdle && value.idleSince !== null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "idleSince belongs only to an idle candidate",
        path: ["idleSince"],
      });
    }
  });
export type LiveStateFacts = z.infer<typeof liveStateFactsSchema>;

export const liveStateDeliveryStates = [
  "pending",
  "delivering",
  "delivered",
  "undeliverable",
] as const;
export const liveStateDeliveryStateSchema = z.enum(liveStateDeliveryStates);
export type LiveStateDeliveryState = z.infer<
  typeof liveStateDeliveryStateSchema
>;

export const liveStateDeliverySchema = z
  .object({
    state: liveStateDeliveryStateSchema,
    deliveredAt: z.string().datetime().nullable(),
    // delivery_seq is bigint in Postgres and must stay a decimal string in JS.
    seq: z.string().regex(/^\d+$/).nullable(),
    attempts: z.number().int().nonnegative(),
  })
  .superRefine((value, context) => {
    if ((value.state === "delivered") !== (value.deliveredAt !== null)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "delivery.state is delivered iff deliveredAt is present",
        path: ["deliveredAt"],
      });
    }
  });
export type LiveStateDelivery = z.infer<typeof liveStateDeliverySchema>;

export const liveStateContainerLifecycles = [
  "active",
  "sleeping",
  "dead",
] as const;
export const liveStateContainerLifecycleSchema = z.enum(
  liveStateContainerLifecycles,
);
export type LiveStateContainerLifecycle = z.infer<
  typeof liveStateContainerLifecycleSchema
>;

export const liveStateSessionSchema = z.object({
  id: z.string().uuid(),
  sessionNumber: z.number().int().positive(),
  chatBackend: z.enum(["interactive", "print"]),
  // null means bounded inspect could not answer; it never means false.
  containerLive: z.boolean().nullable(),
  container: z.object({
    lifecycle: liveStateContainerLifecycleSchema,
  }),
});
export type LiveStateSession = z.infer<typeof liveStateSessionSchema>;

export interface LiveStateResolution {
  state: LiveState;
  since: string;
  reason?: string;
}

function resolveValidatedLiveStateResolution(
  facts: LiveStateFacts,
  now: Date,
): LiveStateResolution {
  const nowMs = now.getTime();
  const isStale = (timestamp: string, staleAfterMs: number): boolean =>
    nowMs - Date.parse(timestamp) > staleAfterMs;

  const unknown = (reason: string): LiveStateResolution => ({
    state: "unknown",
    since: now.toISOString(),
    reason,
  });

  let heartbeatFailureSince: string | null = null;
  if (facts.unreadableReason) {
    return unknown(facts.unreadableReason);
  }

  if (facts.run !== null) {
    const run = facts.run.run;
    const futureRunField = futureRunTimestampField(run, nowMs);
    if (futureRunField !== null) {
      return unknown(`${futureRunField}_in_future`);
    }

    if (facts.run.active) {
      if (facts.run.heartbeatStale) {
        if (run.heartbeatAt === null) {
          return unknown("heartbeat_at_missing");
        }
        if (!isStale(run.heartbeatAt, run.heartbeatStaleAfterMs)) {
          return unknown("heartbeat_staleness_inconsistent");
        }
        heartbeatFailureSince = addMilliseconds(
          run.heartbeatAt,
          run.heartbeatStaleAfterMs,
        );
      } else if (
        run.heartbeatAt !== null &&
        isStale(run.heartbeatAt, run.heartbeatStaleAfterMs)
      ) {
        return unknown("heartbeat_staleness_inconsistent");
      }

      if (!facts.run.heartbeatStale) {
        const outputReference = run.lastOutputAt ?? run.startedAt;
        const expectedOutputStale = isStale(
          outputReference,
          run.outputStaleAfterMs,
        );
        if (facts.run.outputStale !== expectedOutputStale) {
          return unknown("output_staleness_inconsistent");
        }
      }
    }
  }

  for (const [field, timestamp] of [
    ["blocked_since", facts.blockedSince],
    ["queued_since", facts.queuedSince],
    ["your_turn_since", facts.yourTurnSince],
    ["finished_since", facts.finishedSince],
    ["idle_since", facts.idleSince],
  ] as const) {
    if (timestamp !== null && Date.parse(timestamp) > nowMs) {
      return unknown(`${field}_in_future`);
    }
  }

  for (const [signal, timestamp, reason] of [
    [facts.blocked, facts.blockedSince, "blocked_since_missing"],
    [facts.queue.queued, facts.queuedSince, "queued_since_missing"],
    [facts.yourTurn, facts.yourTurnSince, "your_turn_since_missing"],
  ] as const) {
    if (signal && timestamp === null) {
      return unknown(reason);
    }
  }

  if (facts.blocked) {
    return facts.blockedSince === null
      ? unknown("blocked_since_missing")
      : { state: "blocked", since: facts.blockedSince };
  }

  if (facts.run?.active) {
    if (heartbeatFailureSince !== null) {
      return {
        state: "failed",
        since: heartbeatFailureSince,
      };
    }
    if (facts.run.outputStale) {
      return {
        state: "stalled",
        since: addMilliseconds(
          facts.run.run.lastOutputAt ?? facts.run.run.startedAt,
          facts.run.run.outputStaleAfterMs,
        ),
      };
    }
    return { state: "working", since: facts.run.run.startedAt };
  }

  if (facts.queue.queued) {
    return facts.queuedSince === null
      ? unknown("queued_since_missing")
      : { state: "queued", since: facts.queuedSince };
  }

  const endedAt = facts.run?.run.endedAt ?? null;
  const endedAtMs = endedAt === null ? Number.NaN : Date.parse(endedAt);
  const ageMs = now.getTime() - endedAtMs;
  const failedRecently =
    facts.run?.failed === true &&
    Number.isFinite(ageMs) &&
    ageMs >= 0 &&
    ageMs < LIVE_STATE_RECENT_FAILURE_MS;
  if (failedRecently && endedAt !== null) {
    return { state: "failed", since: endedAt };
  }

  if (facts.yourTurn) {
    return facts.yourTurnSince === null
      ? unknown("your_turn_since_missing")
      : { state: "yourTurn", since: facts.yourTurnSince };
  }

  if (facts.everStarted || facts.run !== null) {
    const finishedAt = facts.run?.run.endedAt ?? facts.finishedSince;
    return finishedAt === null
      ? unknown("finished_since_missing")
      : { state: "finished", since: finishedAt };
  }

  return facts.idleSince === null
    ? unknown("idle_since_missing")
    : { state: "idle", since: facts.idleSince };
}

/**
 * Frozen first-match ladder:
 * unknown > blocked > live(stale heartbeat failed > stalled > working) >
 * queued > recent ended failure > yourTurn > finished > idle.
 */
export function resolveLiveStateResolution(
  facts: LiveStateFacts,
  now: Date = new Date(),
): LiveStateResolution {
  return resolveValidatedLiveStateResolution(
    liveStateFactsSchema.parse(facts),
    now,
  );
}

export function resolveLiveState(
  facts: LiveStateFacts,
  now: Date = new Date(),
): LiveState {
  return resolveLiveStateResolution(facts, now).state;
}

export const cardLiveStateSchema = z
  .object({
    computedAt: z.string().datetime(),
    cardId: z.string().uuid(),
    state: liveStateSchema,
    reason: z.string().min(1).optional(),
    since: z.string().datetime(),
    run: liveStateRunSchema.nullable(),
    delivery: liveStateDeliverySchema.nullable(),
    deliveryUnknown: z.boolean(),
    session: liveStateSessionSchema.nullable(),
    queue: liveStateQueueSchema,
    attachable: z.boolean(),
    streamable: z.boolean(),
    stateVersion: z.string().regex(/^live-state-v1-[0-9a-f]{16}$/),
  })
  .superRefine((value, context) => {
    const issue = (message: string, path: Array<string | number>) =>
      context.addIssue({ code: z.ZodIssueCode.custom, message, path });
    const computedAtMs = Date.parse(value.computedAt);
    const isStale = (timestamp: string, staleAfterMs: number): boolean =>
      computedAtMs - Date.parse(timestamp) > staleAfterMs;

    if (Date.parse(value.since) > computedAtMs) {
      issue("since cannot be later than computedAt", ["since"]);
    }
    if (value.run !== null) {
      for (const [field, timestamp] of [
        ["startedAt", value.run.startedAt],
        ["endedAt", value.run.endedAt],
        ["heartbeatAt", value.run.heartbeatAt],
        ["lastOutputAt", value.run.lastOutputAt],
      ] as const) {
        if (timestamp !== null && Date.parse(timestamp) > computedAtMs) {
          issue(`${field} cannot be later than computedAt`, ["run", field]);
        }
      }
    }

    if (value.state === "unknown" && value.reason === undefined) {
      issue("unknown state requires a reason", ["reason"]);
    }
    if (value.state !== "unknown" && value.reason !== undefined) {
      issue("reason is reserved for unknown state", ["reason"]);
    }
    if (value.deliveryUnknown && value.delivery !== null) {
      issue("an unreadable delivery axis cannot carry a delivery claim", [
        "delivery",
      ]);
    }
    if (value.delivery !== null && value.run?.userMessageId == null) {
      issue("delivery must be keyed by the run userMessageId", ["delivery"]);
    }
    if (
      value.delivery?.deliveredAt !== null &&
      value.delivery !== null &&
      value.run !== null
    ) {
      const deliveredAtMs = Date.parse(value.delivery.deliveredAt);
      const deliveryCeilingMs = Date.parse(
        value.run.endedAt ?? value.computedAt,
      );
      if (
        deliveredAtMs < Date.parse(value.run.startedAt) ||
        deliveredAtMs > deliveryCeilingMs
      ) {
        issue("deliveredAt must fall within the corresponding run interval", [
          "delivery",
          "deliveredAt",
        ]);
      }
    }
    if (value.run !== null && value.session === null) {
      issue("every exposed run requires its session tuple", ["session"]);
    }
    if (
      value.run !== null &&
      value.session !== null &&
      value.run.sessionId !== value.session.id
    ) {
      issue("run and session must describe one coherent tuple", [
        "session",
        "id",
      ]);
    }

    const expectedAttachable = value.session?.containerLive === true;
    if (value.attachable !== expectedAttachable) {
      issue("attachable must equal containerLive === true", ["attachable"]);
    }

    const runActive = value.run !== null && isActiveRunStatus(value.run.status);
    if (
      value.streamable &&
      (!runActive ||
        value.session?.chatBackend !== "interactive" ||
        value.attachable !== true)
    ) {
      issue(
        "streamable requires an active, attachable interactive session",
        ["streamable"],
      );
    }
    const liveState = value.state === "working" || value.state === "stalled";
    const heartbeatStale =
      runActive &&
      value.run?.heartbeatAt !== null &&
      value.run !== null &&
      isStale(value.run.heartbeatAt, value.run.heartbeatStaleAfterMs);
    const outputStale =
      runActive &&
      value.run !== null &&
      isStale(
        value.run.lastOutputAt ?? value.run.startedAt,
        value.run.outputStaleAfterMs,
      );
    if (liveState && !runActive) {
      issue(`${value.state} requires a coherent active run`, ["run"]);
    }
    if (value.state === "working" && (heartbeatStale || outputStale)) {
      issue("working requires fresh heartbeat and output facts", ["state"]);
    }
    if (value.state === "stalled" && (!outputStale || heartbeatStale)) {
      issue("stalled requires stale output and a fresh heartbeat", ["state"]);
    }
    if (value.state === "failed" && runActive && !heartbeatStale) {
      issue("active failed requires a stale heartbeat", ["state"]);
    }
    if (
      (value.state === "queued" ||
        value.state === "yourTurn" ||
        value.state === "finished" ||
        value.state === "idle") &&
      runActive
    ) {
      issue(`${value.state} cannot expose an active run`, ["run"]);
    }
    if (value.state === "idle" && value.run !== null) {
      issue("idle cannot expose a run tuple", ["run"]);
    }
    if (value.state === "failed" && value.run === null) {
      issue("failed requires the run that established failure", ["run"]);
    }

    if (value.state === "queued" && !value.queue.queued) {
      issue("queued state requires queue.queued", ["queue", "queued"]);
    }
    if (
      value.queue.queued &&
      !(
        value.state === "unknown" ||
        value.state === "blocked" ||
        value.state === "working" ||
        value.state === "stalled" ||
        (value.state === "failed" && runActive) ||
        value.state === "queued"
      )
    ) {
      issue("queue.queued contradicts the resolved lower-priority state", [
        "queue",
        "queued",
      ]);
    }

    if (
      value.run !== null &&
      value.run.endedAt !== null &&
      !runActive &&
      (value.run.status === "failed" || value.run.status === "timeout")
    ) {
      const endedAtMs = Date.parse(value.run.endedAt);
      const ageMs = computedAtMs - endedAtMs;
      if (
        ageMs >= 0 &&
        ageMs < LIVE_STATE_RECENT_FAILURE_MS &&
        (value.state === "yourTurn" || value.state === "finished")
      ) {
        issue("a recent failed or timed-out run outranks this state", [
          "state",
        ]);
      }
    }

    let expectedSince: string | null = null;
    if (value.state === "unknown") {
      expectedSince = value.computedAt;
    } else if (value.state === "working" && runActive) {
      expectedSince = value.run?.startedAt ?? null;
    } else if (value.state === "stalled" && runActive && value.run !== null) {
      expectedSince = addMilliseconds(
        value.run.lastOutputAt ?? value.run.startedAt,
        value.run.outputStaleAfterMs,
      );
    } else if (value.state === "failed" && runActive) {
      if (value.run?.heartbeatAt === null) {
        issue("active failed state requires heartbeatAt", [
          "run",
          "heartbeatAt",
        ]);
      } else if (value.run !== null) {
        expectedSince = addMilliseconds(
          value.run.heartbeatAt,
          value.run.heartbeatStaleAfterMs,
        );
      }
    } else if (
      value.state === "failed" &&
      value.run !== null &&
      value.run.endedAt !== null
    ) {
      expectedSince = value.run.endedAt;
      const ageMs = computedAtMs - Date.parse(expectedSince);
      if (
        !Number.isFinite(ageMs) ||
        ageMs < 0 ||
        ageMs >= LIVE_STATE_RECENT_FAILURE_MS
      ) {
        issue(
          "terminal failed state must be within the recent-failure window",
          ["run", "endedAt"],
        );
      }
    } else if (
      value.state === "finished" &&
      value.run !== null &&
      value.run.endedAt !== null
    ) {
      expectedSince = value.run.endedAt;
    }

    if (expectedSince !== null && value.since !== expectedSince) {
      issue("since must equal the authoritative transition timestamp", [
        "since",
      ]);
    }

    const { computedAt, stateVersion, ...semanticTuple } = value;
    void computedAt;
    if (stateVersion !== computeLiveStateVersion(semanticTuple)) {
      issue("stateVersion must digest the semantic tuple", ["stateVersion"]);
    }
  });
export type CardLiveState = z.infer<typeof cardLiveStateSchema>;

export interface BuildCardLiveStateInput {
  cardId: string;
  facts: LiveStateFacts;
  delivery: LiveStateDelivery | null;
  deliveryUnknown: boolean;
  session: LiveStateSession | null;
  /** True only when the gateway has a validated interactive transcript cursor. */
  streamable?: boolean;
}

export function computeLiveStateVersion(
  value: Omit<CardLiveState, "computedAt" | "stateVersion">,
): string {
  // Unknown begins at computedAt, but that observation clock is not a semantic
  // transition. Preserve the wire since while keeping an unchanged failure
  // eligible for a stable ETag/stateVersion across successful polls.
  const versionedValue =
    value.state === "unknown"
      ? { ...value, since: "<unknown-observation-time>" }
      : value;
  const serialized = JSON.stringify(versionedValue);
  let hash = 0xcbf29ce484222325n;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= BigInt(serialized.charCodeAt(index));
    hash = BigInt.asUintN(64, hash * 0x100000001b3n);
  }
  return `live-state-v1-${hash.toString(16).padStart(16, "0")}`;
}

/** Build and validate the wire payload without performing any I/O. */
export function buildCardLiveState(
  input: BuildCardLiveStateInput,
  now: Date = new Date(),
): CardLiveState {
  const facts = liveStateFactsSchema.parse(input.facts);
  const delivery =
    input.delivery === null
      ? null
      : liveStateDeliverySchema.parse(input.delivery);
  const session =
    input.session === null ? null : liveStateSessionSchema.parse(input.session);
  const resolution = resolveValidatedLiveStateResolution(facts, now);
  const futureRunField =
    facts.run === null
      ? null
      : futureRunTimestampField(facts.run.run, now.getTime());
  const semanticTuple: Omit<CardLiveState, "computedAt" | "stateVersion"> = {
    cardId: input.cardId,
    state: resolution.state,
    ...(resolution.reason === undefined ? {} : { reason: resolution.reason }),
    since: resolution.since,
    run: futureRunField === null ? (facts.run?.run ?? null) : null,
    delivery: futureRunField === null ? delivery : null,
    deliveryUnknown:
      input.deliveryUnknown || (futureRunField !== null && delivery !== null),
    session,
    queue: facts.queue,
    attachable: session?.containerLive === true,
    streamable: input.streamable === true,
  };

  return cardLiveStateSchema.parse({
    computedAt: now.toISOString(),
    ...semanticTuple,
    stateVersion: computeLiveStateVersion(semanticTuple),
  });
}
